import { FormattedMessage, useIntl } from 'react-intl';
import cn from 'classnames';
import React, { useState, useMemo, useReducer, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  CollectionCard,
  PageTitle,
  Tag,
  Button,
  LayoutView,
  CollectionRow,
  LayoutViewSwitcher,
  Dropdown,
  SearchBar,
  SearchFiltersSidebar,
  Pager,
  CreateCollectionModal,
  FeatureEmptyGuideCard,
  FiltersDrawerHeader,
  PageIntro,
} from 'src/components';
import { useSearchCollectionsQuery } from 'src/api/queries/collectionsQueries';
import { usePlanLimitsQuery } from 'src/api/queries/subscriptionQueries';

import { Collection } from 'src/types/TCollection';
import styles from './Collections.module.scss';
import messages from './messages';

import useModal from 'src/hooks/useModal';

import { CollectionQuery } from 'src/api/functions/collectionsApi';
import { sortAtom } from 'src/shared/sort';
import { useAtomValue, useUpdateAtom } from 'jotai/utils';
import { filtersVisibleAtom } from 'src/shared/filters';
import { useAtom } from 'jotai';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { isRefetchingAtom, refreshAtom } from 'src/shared/refresh';
import { searchAtom } from 'src/shared/search';
import { TSorting } from 'src/types/TSorting';
import BoardingTour from 'src/components/BoardingTour';
import useFiltersDrawer from 'src/hooks/useFiltersDrawer';

const initialSearchQuery: CollectionQuery = {
  sort_by: ['last_update DESC'],
  offset: 0,
  limit: 30,
};

const searchQueryReducer = (state: CollectionQuery, payload: CollectionQuery) => ({ ...state, ...payload });

export const Collections = () => {
  const intl = useIntl();
  const [itemsPerPage, setItemsPerPage] = useLocalStorage('collections.itemsPerPage', '30');
  const [sorting, setSorting] = useState<TSorting>(['last_update', 'DESC']);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const refreshValue = useAtomValue(refreshAtom);
  const setIsRefetching = useUpdateAtom(isRefetchingAtom);

  const [searchQuery, dispatchSearchQuery] = useReducer(searchQueryReducer, initialSearchQuery, (state) => {
    const limit = Number(itemsPerPage);
    return { ...state, limit: limit };
  });

  const currentPage = useMemo(() => {
    if (!searchQuery.offset || !searchQuery.limit) return 1;

    return Math.floor(searchQuery.offset / searchQuery.limit) + 1;
  }, [searchQuery]);

  const hasActiveFilters = useMemo(
    () =>
      !!(
        (searchQuery.keyword && searchQuery.keyword !== '') ||
        searchQuery.private_only ||
        searchQuery.last_update_range?.start ||
        searchQuery.last_update_range?.end
      ),
    [
      searchQuery.keyword,
      searchQuery.private_only,
      searchQuery.last_update_range?.start,
      searchQuery.last_update_range?.end,
    ],
  );

  const {
    data: allCollectionsResult,
    refetch,
    isLoading,
    isRefetching,
    error,
  } = useSearchCollectionsQuery(searchQuery, {
    onSuccess: (results) => {
      // TODO: handle pagination
      setTotalPages(Math.ceil(results.data.total_collections / (searchQuery.limit ?? 1)));
    },
  });

  const isEmpty = !isLoading && !isRefetching && !hasActiveFilters &&
    (!allCollectionsResult?.data?.collections || allCollectionsResult.data.collections.length === 0);

  const filtersSidebarRef = useRef<HTMLDivElement>(null);

  // Mobile drawer wrapper: locks background scroll, listens for ESC.
  const filtersDrawer = useFiltersDrawer({ visible: filtersVisible, setVisible: setFiltersVisible });

  const { showModal } = useModal();

  // Live-count plan cap (rollout 28). quota 0 == unlimited. The backend gate
  // is the enforcement; this is UX — disable create AT the cap instead of
  // letting the click bounce off the upgrade 403. NB: the counter here is
  // subscription-wide (what the cap counts), while the header counter is
  // your visible collections — they can differ on shared/team accounts.
  const { data: planLimits } = usePlanLimitsQuery();
  const collectionsLimit = planLimits?.collections;
  const atCollectionsCap = Boolean(
    collectionsLimit &&
      collectionsLimit.quota > 0 &&
      collectionsLimit.used >= collectionsLimit.quota,
  );
  const capTitle =
    'Collection limit reached — delete a collection you no longer need, or upgrade your plan.';

  const handleOnCreateCollection = () => window.location.reload();

  const showCreateCollectionModal = () =>
    showModal(<CreateCollectionModal onCreateCollection={handleOnCreateCollection} />);

  const handleSearchCollection = (searchTerm: string) => {
    setSearchTerm(searchTerm);
    dispatchSearchQuery({
      keyword: searchTerm,
    });
  };

  const handleApplyFilters = (filters: any) => {
    dispatchSearchQuery({
      ...filters,
    });
    setFiltersVisible(false);
  };

  const handlePageChange = (page: number) => {
    dispatchSearchQuery({
      offset: (page - 1) * (searchQuery.limit ?? 30),
    });
  };

  const handleItemsPerPageChange = (newLimit: number) => {
    setItemsPerPage(newLimit.toString());
    dispatchSearchQuery({
      limit: newLimit,
      offset: 0,
    });
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    dispatchSearchQuery({
      keyword: '',
    });
  };

  const handleClearDateFilter = (date: 'start' | 'end') => {
    const oppositeDate = date === 'start' ? 'end' : 'start';

    dispatchSearchQuery({
      last_update_range: {
        [date]: undefined,
        [oppositeDate]: searchQuery.last_update_range?.[oppositeDate],
      },
    });
  };

  useEffect(() => {
    dispatchSearchQuery({
      sort_by: [sorting.join(' ')],
    });
  }, [sorting]);

  useEffect(() => {
    if (isRefetching) return;
    refetch();
  }, [refreshValue]);

  useEffect(() => {
    setIsRefetching(isRefetching);
  }, [isRefetching]);

  return (
    <><BoardingTour title='Collections' description='This page shows all the collections' path='/collections' uniqueId='COLLECTIONS' />
    <div className={styles.pageWrapper}>
      <PageTitle
        title={'Collections'}
        className={styles.pageTitle}
        counter={allCollectionsResult?.data?.total_collections}
        rightContainer={
          <Button
            size="small"
            onClick={() => showCreateCollectionModal()}
            data-test="add-collection"
            disabled={atCollectionsCap}
            tooltip={atCollectionsCap ? capTitle : undefined}
          >
            <FormattedMessage {...messages.add} />
          </Button>
        }
      />
      <PageIntro inset overview="Everyday asset management for yourself and for internal team collaboration." />
      {/* Plan-cap notice (rollout 28) — shown only AT the cap, where the
          disabled create button needs an explanation and a way forward. */}
      {atCollectionsCap && collectionsLimit && (
        <div className={styles.limitBanner} data-test="collections-limit-banner" role="status">
          You&apos;ve reached your plan&apos;s limit of {collectionsLimit.quota} collections.
          Delete collections you no longer need, or{' '}
          <Link to="/admin/subscription/plans">upgrade your plan</Link> for more.
        </div>
      )}
      <SearchBar
        search={searchTerm}
        sortKeys={[
          { key: 'last_update', value: 'Last Update' },
          { key: 'name', value: 'Name' },
          { key: 'group_access', value: 'Private' },
        ]}
        onSelectSort={setSorting}
        searchButton
        onClickSearchButton={handleSearchCollection}
        onClickFiltersButton={() => setFiltersVisible(!filtersVisible)}
        hideFaceSearchButton={true}
      />
      <section className={styles.pageContainer}>
        {filtersDrawer.drawerOpen && (
          <div className={styles.mobileScrim} onClick={filtersDrawer.closeIfMobile} aria-hidden />
        )}
        {filtersVisible && (
          <div ref={filtersSidebarRef} className={styles.filterSidebarWrapper}>
            <FiltersDrawerHeader onClose={filtersDrawer.closeIfMobile} />
            <SearchFiltersSidebar
              onApplyFilters={handleApplyFilters}
              filters={[
                {
                  title: 'Collection Access',
                  key: 'private_only',
                  inputs: {
                    type: 'checkbox',
                    label: 'Private Only',
                    initialValue: searchQuery.private_only,
                  },
                },
                {
                  title: 'Last Updated Date Range',
                  key: 'last_update',
                  inputs: [
                    {
                      key: 'start',
                      type: 'date',
                      label: 'Start Date :',
                      initialValue: searchQuery.last_update_range?.start,
                    },
                    {
                      key: 'end',
                      type: 'date',
                      label: 'End Date :',
                      initialValue: searchQuery.last_update_range?.end,
                    },
                  ],
                },
              ]}
            />
          </div>
        )}

        <div className={cn([styles.pageContent])}>
          <div className={styles.actionsWrapper}>
            <div className={styles.tags}>
              {searchQuery.keyword !== '' && (
                <>
                  {searchQuery.keyword && searchQuery.keyword !== '' && (
                    <Tag text={searchQuery.keyword} onClose={handleClearSearch} />
                  )}
                  {searchQuery.private_only && (
                    <Tag text="Private Only" onClose={() => dispatchSearchQuery({ private_only: false })} />
                  )}
                  {searchQuery.last_update_range && (
                    <>
                      {searchQuery.last_update_range.start && (
                        <Tag
                          text={`Start Date: ${searchQuery.last_update_range.start}`}
                          onClose={() => handleClearDateFilter('start')}
                        />
                      )}
                      {searchQuery.last_update_range.end && (
                        <Tag
                          text={`End Date: ${searchQuery.last_update_range.end}`}
                          onClose={() => handleClearDateFilter('end')}
                        />
                      )}
                    </>
                  )}
                </>
              )}
            </div>
            {!isEmpty && (
              <div>
                <LayoutViewSwitcher />
              </div>
            )}
          </div>
          <LayoutView
            emptyDataMessage={intl.formatMessage(hasActiveFilters ? messages.emptyFiltered : messages.emptyDefault)}
            emptyState={
              hasActiveFilters ? undefined : (
                <FeatureEmptyGuideCard
                  title="No collections created"
                  info={<FormattedMessage {...messages.emptyGuideStep1} />}
                  mediaStep={{
                    src: '/userhelp/create-collection.jpg',
                    alt: intl.formatMessage(messages.emptyGuideImageAlt),
                    body: (
                      <FormattedMessage
                        {...messages.emptyGuideStep2}
                        values={{
                          withSelected: <strong>With Selected</strong>,
                          assetsPage: <Link to="/assets">Assets page</Link>,
                        }}
                      />
                    ),
                  }}
                />
              )
            }
            isLoading={isLoading || isRefetching}
            data={allCollectionsResult?.data?.collections ?? []}
            cardView={(collection: Collection) => <CollectionCard key={collection.id} collection={collection} />}
            listView={(collection: Collection) => <CollectionRow key={collection.id} collection={collection} />}
            classNames={{
              cardView: 'listingGridWrapper',
              listView: styles.listWrapper,
            }}
          />

          {(allCollectionsResult?.data?.total_collections ?? 0) > Number(itemsPerPage) && (
            <div className={styles.pagerWrapper}>
              <Pager
                currentPage={currentPage}
                totalPages={totalPages}
                onChange={handlePageChange}
                itemsPerPage={Number(itemsPerPage)}
                onItemsPerPageChange={handleItemsPerPageChange}
                showItemsPerPage={true}
              />
            </div>
          )}
        </div>
      </section>
    </div>
    </>
  );
};
